import { ECONOMY } from '@/config/economy';
import { CARDS, type CardContext, type CardDef, type Currency, type Rarity } from './cards';
import type { Rng } from '@/core/rng';

export interface OfferCard {
  card: CardDef;
  price: number;
  currency: Currency;
  bought: boolean;
  /** Locked offers survive rerolls AND the next shop (docs/12 B3) */
  locked: boolean;
}

/** Yellow prices inflate per room and respect the discount card; red is flat. */
export function priceOf(card: CardDef, room: number, discountMul: number): number {
  if (card.currency === 'red') {
    if (card.id === 'barrel') return ECONOMY.redPrice.tier;
    if (card.rarity === 'legendary') return ECONOMY.redPrice.legendary;
    if (card.rarity === 'mythic') return ECONOMY.redPrice.mythic;
    return ECONOMY.redPrice.epic;
  }
  const yellow = ECONOMY.cardPrice as Record<string, number>;
  const base = yellow[card.rarity] ?? ECONOMY.cardPrice.rare;
  return Math.max(1, Math.round(base * (1 + ECONOMY.priceRoomScale * room) * discountMul));
}

function eligible(ctx: CardContext, used: Set<string>): CardDef[] {
  return CARDS.filter(
    (c) =>
      !used.has(c.id) &&
      (c.canOffer?.(ctx) ?? true) &&
      (c.maxStacks === undefined || ctx.owned(c.id) < c.maxStacks),
  );
}

function weightedPick(rng: Rng, cards: CardDef[]): CardDef {
  const total = cards.reduce((sum, c) => sum + (c.weight ?? 1), 0);
  let r = rng.next() * total;
  for (const c of cards) {
    r -= c.weight ?? 1;
    if (r <= 0) return c;
  }
  return cards[cards.length - 1]!;
}

/** Roll rarity, weighted-pick an eligible card of that rarity; build `count` offers. */
export function rollOffer(
  rng: Rng, ctx: CardContext, room: number, count = 3,
  excludeIds: ReadonlyArray<string> = [],
): OfferCard[] {
  const offer: OfferCard[] = [];
  const used = new Set<string>(excludeIds);
  const discount = ctx.stats.discountMul;

  for (let i = 0; i < count; i++) {
    const pool = eligible(ctx, used);
    if (pool.length === 0) break;
    const rarity = rollRarity(rng);
    const ofRarity = pool.filter((c) => c.rarity === rarity);
    const card = weightedPick(rng, ofRarity.length > 0 ? ofRarity : pool);
    used.add(card.id);
    offer.push({
      card,
      price: priceOf(card, room, discount),
      currency: card.currency,
      bought: false,
      locked: false,
    });
  }
  return offer;
}

function rollRarity(rng: Rng): Rarity {
  const w = ECONOMY.rarityWeight;
  let r = rng.next();
  for (const tier of ['mythic', 'legendary', 'epic', 'rare', 'uncommon'] as const) {
    if (r < w[tier]) return tier;
    r -= w[tier];
  }
  return 'common';
}
